import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import {
  deleteUserPartnerGroup,
  editLoyaltyBalance,
  editUser,
  fetchUserProfile,
  saveUserPartnerGroup,
  type UserProfile,
} from './api';
import { ProfileFields } from './ProfileFields';
import { FormErrorSummary, type ProfileFormApi } from './ProfileInputs';
import {
  buildEditBody,
  getProfilePermissions,
  isServiceDiscountOn,
  isServiceRole,
  profileToValues,
  type ProfileValues,
} from './profileForm';
import { FineWriteOffBox, RevertStatusBox } from './ServiceActionBoxes';

const DEACTIVATED_STATUS = '6';
const FINE_ROLES = ['admin', 'finance-chief'];

/** One-off inputs that must not survive a successful save. */
const TRANSIENT_VALUES: Partial<ProfileValues> = {
  password: '',
  passwordRepeat: '',
  generatePassword: false,
  comment: '',
  bonusComment: '',
  loyaltyAmount: '',
  loyaltyComment: '',
};

type EditFormProps = {
  user: UserProfile;
  /** Refresh dependent queries after a profile save. */
  onSaved: () => void;
  /** Reload the profile and reset the form, for actions that change balance / status server-side. */
  onReload: () => void;
};

const EditForm = ({ user, onSaved, onReload }: EditFormProps) => {
  const adminRoles = useAuthStore((s) => s.profile.roles);
  const permissions = getProfilePermissions(adminRoles);
  const [values, setValues] = useState(() => profileToValues(user));
  const [saved, setSaved] = useState(false);

  const saveProfile = async (next: ProfileValues) => {
    await editUser(buildEditBody(user.id, next, permissions, user.seo_data));

    const initialGroupId = String(user.group_partner?.id ?? '');
    if (next.partnerGroupId !== initialGroupId) {
      await (next.partnerGroupId
        ? saveUserPartnerGroup(next.partnerGroupId, user.id)
        : deleteUserPartnerGroup(user.id));
    }
    if (next.loyaltyAmount || next.loyaltyComment) {
      await editLoyaltyBalance(user.id, next.loyaltyAmount, next.loyaltyComment);
    }
  };

  const { errors, setErrors, clearErrors, save, isPending } = useSaveForm(saveProfile, () => {
    setSaved(true);
    setValues((prev) => ({ ...prev, ...TRANSIENT_VALUES }));
    onSaved();
  });

  const form: ProfileFormApi = {
    values,
    errors,
    patch: (change, ...touched) => {
      setValues((prev) => ({ ...prev, ...change }));
      setSaved(false);
      clearErrors(...touched);
    },
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isServiceDiscountOn(values) && !values.serviceDiscountPercent) {
      setErrors({ discount_percent: ['Обязательно к заполнению'] });
      return;
    }
    save(values);
  };

  const isService = isServiceRole(values.roles);

  return (
    <>
      <div className="box box-primary">
        <div className="box-body">
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <label className="control-label">ID</label>
              <input className="form-control input-sm" style={{ maxWidth: 200 }} value={user.id} disabled />
            </div>
            <FormErrorSummary errors={errors} />
            <ProfileFields form={form} mode="edit" permissions={permissions} />
            <button type="submit" className="btn btn-info" disabled={isPending}>
              Сохранить
            </button>
            {saved && <span className="text-green"> Данные сохранены успешно</span>}
          </form>
        </div>
      </div>
      {isService && (
        <FineWriteOffBox
          userId={user.id}
          onDone={onReload}
          disabled={!FINE_ROLES.some((role) => role in adminRoles)}
        />
      )}
      {isService && String(user.status_id) === DEACTIVATED_STATUS && (
        <RevertStatusBox userId={user.id} onDone={onReload} />
      )}
    </>
  );
};

export const UserEditPage = () => {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [formVersion, setFormVersion] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ['user', id, 'edit'],
    queryFn: () => fetchUserProfile(id, true),
    gcTime: 0, // the form copies this data into local state, so never start from a cached snapshot
  });

  const onSaved = () => {
    void qc.invalidateQueries({ queryKey: ['user', id, 'view'] });
    void qc.invalidateQueries({ queryKey: ['users'] });
  };

  const onReload = async () => {
    await qc.refetchQueries({ queryKey: ['user', id, 'edit'] });
    setFormVersion((version) => version + 1);
    onSaved();
  };

  return (
    <>
      <section className="content-header">
        <h1>Редактирование профиля</h1>
      </section>
      <section className="content">
        <QueryStatus isLoading={isLoading} error={error} errorText="Не удалось загрузить профиль" />
        {data && <EditForm key={`${id}-${formVersion}`} user={data} onSaved={onSaved} onReload={onReload} />}
      </section>
    </>
  );
};
